import { getService } from '@Shared.Angular/flowingly.hooks/useService';
import React, { useRef, useEffect, useCallback, useState } from 'react';

type DraggableSliderProps = {
  min: number;
  max: number;
  value: number;
  onChange: (val: number) => void;
};

const DraggableSlider: React.FC<DraggableSliderProps> = ({
  min,
  max,
  value,
  onChange
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  const getPercentage = useCallback(() => {
    const percent = ((value - min) / (max - min)) * 100;
    return `calc(${percent}% - ${(percent / 100) * 20}px)`;
  }, [value, min, max]);

  const handleMouseMove = (e: MouseEvent | TouchEvent) => {
    if (!draggingRef.current || !trackRef.current) return;

    const clientX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX;
    const rect = trackRef.current.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const percent = Math.min(100, Math.max(0, (offsetX / rect.width) * 100));
    const newValue = min + (percent / 100) * (max - min);

    onChange(Math.round(newValue));
  };

  const handleMouseUp = (event: MouseEvent | TouchEvent) => {
    draggingRef.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.removeEventListener('touchmove', handleMouseMove);
    document.removeEventListener('touchend', handleMouseUp);
  };

  const startDrag = (event: React.MouseEvent | React.TouchEvent) => {
    draggingRef.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, []);

  return (
    <div
      ref={trackRef}
      className="draggable-slider"
      onClick={(e) => {
        const rect = trackRef.current?.getBoundingClientRect();
        if (!rect) return;
        const percent = Math.min(
          100,
          Math.max(0, ((e.clientX - rect.left) / rect.width) * 100)
        );
        const newValue = min + (percent / 100) * (max - min);
        onChange(Math.round(newValue));
      }}
    >
      <div className="draggable-slider-track" />
      <div
        className="draggable-slider-handle"
        style={{ left: getPercentage() }}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        onClick={(e) => {
          e.stopPropagation();
        }}
      />
    </div>
  );
};

export default DraggableSlider;
